import { t } from 'i18next';
import { dateObjToStr } from '../../../utils/date';
import Button from '../../atoms/button';
import Select from '../../atoms/select';
import SectionHeader from './section-header';
import Text from '../../atoms/text';
import { useState, useCallback, useEffect } from 'react';
import YardService, { IYard } from '../../../services/Yard.service';
import { EditableChangeEvent } from '../../../utils/types/editable';
import { MonitoringForm, SetPointError, TempStatus } from './containers';
import MonitoringService, {
	IMonitoring,
} from '../../../services/Monitoring.service';
import { useRouteLoaderData } from 'react-router-dom';
import { HomeLoaderData } from '../../../types/homeLoaderData';
import ContainerService, {
	IContainer,
} from '../../../services/Container.service';

interface SelectYardProps {
	yardSelected: IYard | undefined;
	setYardSelected: React.Dispatch<React.SetStateAction<IYard | undefined>>;
	monitorings: MonitoringForm[];
	setMonitorings: React.Dispatch<React.SetStateAction<MonitoringForm[]>>;
}

const obterMonitoramentosLocais = (yardId: number): MonitoringForm[] | undefined => {
	const plainText = localStorage.getItem(`monitoring-yard-${yardId}`);
	if (!plainText) return;

	try {
		const { data } = JSON.parse(plainText);
		if (!Array.isArray(data) || data.length === 0) return;

		const monitoramentos = data.filter(
			(item: MonitoringForm) => item?.idContainer !== undefined
		);
		return monitoramentos.length > 0 ? monitoramentos : undefined;
	} catch {
		return;
	}
};

const criarMonitoramentoInicial = (
	container: IContainer
): MonitoringForm => ({
	container,
	label: container.label,
	setPoint: container.temperature,
	cntrStatus: 'Temperatura Regular',
	temperature: container.temperature,
	observation: '',
	isChecked: false,
	idContainer: container.id,
	id: -1,
	images: [],
	files: [],
	humidity:
		container.humidity == null ? undefined : Number(container.humidity),
	ventilation:
		container.ventilation == null
			? undefined
			: Number(container.ventilation),
	atmosferaControlada: container.atmosferaControlada,
	nivelCo2: container.nivelCo2,
	return: container.return == null ? undefined : Number(container.return),
	supply: container.supply == null ? undefined : Number(container.supply),
	returnSetPoint: container.return,
	supplySetPoint: container.supply,
	reparoPreventivo: false,
});

const aplicarMonitoramentoAnterior = (
	monitoramentoInicial: MonitoringForm,
	monitoring: IMonitoring
): MonitoringForm => {
	const tempDiff = Math.abs(
		Number(monitoramentoInicial.temperature) -
			Number(monitoring.temperature || 0)
	);
	const cntrStatus =
		tempDiff == undefined || tempDiff <= 2
			? 'Temperatura Regular'
			: tempDiff < 4
				? 'Temperatura em Alerta'
				: 'Temperatura Crítica';

	const problemas = monitoring.problems || [];
	let tempStatus: SetPointError | undefined;
	let tempAlert: TempStatus = cntrStatus;
	if (problemas.length > 0) {
		tempStatus = problemas.includes(2)
			? 'Coolingdown'
			: problemas.includes(3)
				? 'Defrost'
				: undefined;
		tempAlert = problemas.includes(4)
			? 'Problema Crítico'
			: problemas.includes(6)
				? 'Sem Alimentação'
				: problemas.includes(8)
					? 'Falta de Energia'
					: cntrStatus;
	}

	return {
		...monitoramentoInicial,
		cntrStatus: tempAlert,
		tempStatus,
		temperature: String(monitoring.temperature),
		observation: monitoring.observation || '',
		isChecked: true,
		images: monitoring.images || [],
		files: [],
		humidity: monitoring.humidity,
		ventilation: monitoring.ventilation,
		atmosferaControlada: monitoring.atmosferaControlada,
		nivelCo2: monitoring.nivelCo2,
		return: monitoring.return,
		supply: monitoring.supply,
		reparoPreventivo: problemas.includes(10),
	};
};

const SelectYard = ({
	monitorings,
	setMonitorings,
	yardSelected,
	setYardSelected,
}: SelectYardProps) => {
	const { accountId } = useRouteLoaderData('home') as HomeLoaderData;
	const [yards, setYards] = useState<IYard[]>([]);
	const [totalMonitored, setTotalMonitored] = useState<number>(0);
	const [date, setDate] = useState<Date>(new Date());

	useEffect(() => {
		setTotalMonitored(monitorings.filter((item) => item.isChecked).length);
	}, [monitorings]);

	const loadYards = useCallback(async () => {
		const response = await YardService.getAll({
			isActive: 1,
			idClient: Number(accountId),
		});
		if (!response) return;
		setYards(response.data);
	}, []);

	useEffect(() => {
		loadYards();
	}, [loadYards]);

	const loadContainers = useCallback(async () => {
		if (!yardSelected || !yardSelected.id) return;
		const data: IContainer[] = await ContainerService.getAllByYard(
			yardSelected.id
		);
		if (!data) setMonitorings([]);
		else setMonitorings(data.map(criarMonitoramentoInicial));
	}, [yardSelected]);

	useEffect(() => {
		loadContainers();
	}, [loadContainers]);

	const handleYard = (e: EditableChangeEvent) => {
		const newYard = yards.find((item) => item.name === e.target.value);
		if (newYard) setYardSelected(newYard);
	};

	const onLoadPreviousClick = async () => {
		if (!yardSelected?.id) return;

		const monitoramentosLocais = obterMonitoramentosLocais(
			yardSelected.id
		);
		if (monitoramentosLocais) {
			setMonitorings((prev) =>
				prev.map((item) => {
					const monitoramento = monitoramentosLocais.find(
						(previous) => previous.idContainer === item.idContainer
					);
					if (!monitoramento) return item;
					return {
						...item,
						...monitoramento,
						possuiMonitoramentoLocal: true,
					};
				})
			);
			return;
		}

		const monitorings: IMonitoring[] =
			await MonitoringService.getAllByYard(yardSelected.id);
		if (!monitorings) return;

		setMonitorings((prev) =>
			prev.map((item) => {
				const monitoring = monitorings.find(
					(previous) => previous.container?.id === item.idContainer
				);
				return monitoring
					? aplicarMonitoramentoAnterior(item, monitoring)
					: item;
			})
		);
	};

	return (
		<div className="grid grid-rows-[auto_1fr] gap-3 border border-primary rounded-lg">
			<SectionHeader title={t('monitor.select_yard')} />
			<div className="grid grid-rows-[auto_1fr_auto] gap-3 p-3">
				<Select
					boxClassName="h-fit"
					options={yards.map((value) => value.name)}
					placeholder={t('port_management.yard')}
					value={yardSelected?.name || ''}
					onChange={handleYard}
				/>
				<div className="bg-primary-lighter rounded-md flex flex-col items-center justify-center gap-2">
					<Text>
						<b className="pr-2 text-center">
							{t('monitor.containers_in_yard')}:
						</b>
						{monitorings.length}
					</Text>
					<Text>
						<b className="pr-2 text-center">
							{t('monitor.containers_monitored')}:
						</b>
						{totalMonitored}
					</Text>
					<Text className="flex flex-col text-center">
						<b className="pr-2 text-center">
							{t('monitor.restored')}:
						</b>
						<Text>{dateObjToStr(date)}</Text>
					</Text>
				</div>
				<Button
					className="w-full h-fit"
					onClick={() => onLoadPreviousClick()}
				>
					{t('monitor.load_previous')}
				</Button>
			</div>
		</div>
	);
};

export default SelectYard;
